import React, { useEffect, useMemo, useState } from 'react'
import { Empty, Spin } from 'antd'
import { getRuntimeAssetDashboardSummary, getRuntimeAssetDetail } from '../../request'
import { EMPTY, formatDateTime, formatInteger, termDisplay } from './terminalModel'

const ASSET_TYPE_LABELS = {
  vibe_project: 'Vibe 项目',
  microservice: '微服务',
  collector: '采集器',
  scheduled_task: '周期任务',
  external_system: '外部系统'
}

const STATUS_META = {
  healthy: { label: '健康', color: '#089981' },
  warning: { label: '预警', color: '#E0A21C' },
  failure: { label: '故障', color: '#f23645' },
  unknown: { label: '未知', color: '#B2B5BE' }
}

const statusMeta = status => STATUS_META[status] || STATUS_META.unknown

const settle = promise => promise.then(value => ({ ok: true, value }), () => ({ ok: false, value: null }))

function RuntimeAssetDetail ({ assetId, onClose }) {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    setDetail(null)
    getRuntimeAssetDetail(assetId)
      .then(value => { if (mounted) setDetail(value || null) })
      .catch(() => { if (mounted) setError('运行资产详情加载失败') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [assetId])

  const events = detail && Array.isArray(detail.recentEvents) ? detail.recentEvents : []
  const meta = statusMeta(detail && detail.healthStatus)
  return <div className="term-overlay" role="dialog" aria-modal="true" aria-label="运行资产详情">
    <div className="term-top term-top-overlay">
      <button type="button" className="term-back" onClick={onClose}>← 返回</button>
      <div className="term-h1">{termDisplay(detail && detail.name)}</div>
      <div className="term-top-meta">
        <span>{termDisplay(detail && ASSET_TYPE_LABELS[detail.assetType])}</span>
        <span>只读</span>
      </div>
    </div>
    <div className="term-overlay-body">
      <Spin spinning={loading}>
        {error && <div className="term-notice term-notice-error">{error}</div>}
        {detail ? <div className="term-panel">
          <dl className="term-kv">
            <dt>状态</dt><dd><span className="term-state"><span className="term-dot" style={{ background: meta.color }} />{meta.label}</span></dd>
            <dt>资产标识</dt><dd className="term-mono">{termDisplay(detail.assetKey)}</dd>
            <dt>类型</dt><dd>{termDisplay(ASSET_TYPE_LABELS[detail.assetType] || detail.assetType)}</dd>
            <dt>所属系统</dt><dd>{termDisplay(detail.systemName)}</dd>
            <dt>环境</dt><dd>{termDisplay(detail.environment)}</dd>
            <dt>负责人</dt><dd>{termDisplay(detail.ownerName)}</dd>
            <dt>部门</dt><dd>{termDisplay(detail.ownerDepartmentName)}</dd>
            <dt>最近成功</dt><dd>{formatDateTime(detail.lastSuccessAt)}</dd>
            <dt>下次期望</dt><dd>{formatDateTime(detail.nextExpectedAt)}</dd>
            <dt>判定依据</dt><dd>{termDisplay(detail.healthReason)}</dd>
            <dt>说明</dt><dd>{termDisplay(detail.description)}</dd>
          </dl>
          <div className="term-panel-title">最近运行记录（{events.length}）</div>
          {events.length === 0
            ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未收到运行上报" />
            : <table className="term-table term-table-plain">
              <thead>
                <tr>
                  <th>事件</th>
                  <th>结果</th>
                  <th>观测时间</th>
                  <th className="term-num">耗时</th>
                  <th className="term-num">数据量</th>
                  <th>错误摘要</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => <tr key={event.eventId} className="term-row">
                  <td className="term-td term-nowrap">{termDisplay(event.eventType)}</td>
                  <td className="term-td term-nowrap">{termDisplay(event.result)}</td>
                  <td className="term-td term-nowrap">{formatDateTime(event.observedAt)}</td>
                  <td className="term-td term-num">{event.durationMs === null || event.durationMs === undefined ? EMPTY : `${event.durationMs} ms`}</td>
                  <td className="term-td term-num">{formatInteger(event.recordCount)}</td>
                  <td className="term-td term-ellipsis" title={termDisplay(event.errorSummary)}>{termDisplay(event.errorSummary)}</td>
                </tr>)}
              </tbody>
            </table>}
        </div> : !loading && !error && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无运行资产详情" />}
      </Spin>
    </div>
  </div>
}

/** 运行资产：复用 reliability 汇总接口，只读列表 + 行点击详情。 */
export default function TerminalAssets () {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState('all')
  const [status, setStatus] = useState('all')
  const [detailId, setDetailId] = useState(null)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    settle(getRuntimeAssetDashboardSummary({})).then(result => {
      if (!mounted) return
      if (!result.ok) {
        setError('运行资产加载失败')
        setSummary({ counts: {}, assets: [] })
        return
      }
      setSummary(result.value || { counts: {}, assets: [] })
    }).finally(() => {
      if (mounted) setLoading(false)
    })
    return () => { mounted = false }
  }, [])

  const assets = useMemo(() => (summary && Array.isArray(summary.assets) ? summary.assets : []), [summary])
  const counts = (summary && summary.counts) || {}
  const rows = useMemo(() => assets.filter(item => {
    const haystack = [item.name, item.systemName, item.environment, item.ownerName, item.ownerDepartmentName]
      .filter(Boolean).join(' ').toLowerCase()
    return (!keyword || haystack.includes(keyword.toLowerCase())) &&
      (type === 'all' || item.assetType === type) &&
      (status === 'all' || item.healthStatus === status)
  }), [assets, keyword, type, status])

  const kpis = [
    ['全部资产', counts.total, 'units'],
    ['健康', counts.healthy, 'healthy'],
    ['预警', counts.warning, 'warning'],
    ['故障', counts.failure, 'failure'],
    ['未知', counts.unknown, 'unknown']
  ]

  return <div className="term-page">
    {error && <div className="term-notice term-notice-error">{error}</div>}
    <div className="term-page-head">
      <div className="term-page-title">运行资产</div>
      <div className="term-page-sub">统一观察 Vibe 项目、微服务、采集器、周期任务与外部系统；只读，上报由运行端完成。</div>
    </div>
    <Spin spinning={loading}>
      <div className="term-kpis term-kpis-dense">
        {kpis.map(([label, value, key]) => <div key={key} className={`term-kpi term-kpi-${key}`}>
          <div className="term-kpi-label">{label}</div>
          <div className="term-kpi-value">{formatInteger(value)}</div>
        </div>)}
      </div>
      <div className="term-toolbar">
        <input
          className="term-search"
          type="search"
          value={keyword}
          placeholder="搜索资产、系统、环境或负责人"
          aria-label="搜索资产、系统、环境或负责人"
          onChange={event => setKeyword(event.target.value)}
        />
        <select className="term-select" value={type} aria-label="资产类型筛选" onChange={event => setType(event.target.value)}>
          <option value="all">全部类型</option>
          {Object.keys(ASSET_TYPE_LABELS).map(value => <option key={value} value={value}>{ASSET_TYPE_LABELS[value]}</option>)}
        </select>
        <select className="term-select" value={status} aria-label="资产状态筛选" onChange={event => setStatus(event.target.value)}>
          <option value="all">全部状态</option>
          {Object.keys(STATUS_META).map(value => <option key={value} value={value}>{STATUS_META[value].label}</option>)}
        </select>
        <span className="term-count">显示 {rows.length} / {assets.length}</span>
      </div>
      <table className="term-table term-table-plain">
        <thead>
          <tr>
            <th>资产名称</th>
            <th>类型</th>
            <th>所属系统</th>
            <th>环境</th>
            <th>状态</th>
            <th>最近成功</th>
            <th>下次期望</th>
            <th>负责人</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0
            ? <tr><td className="term-empty" colSpan={9}>尚未登记运行资产</td></tr>
            : rows.map(item => {
              const meta = statusMeta(item.healthStatus)
              return <tr key={item.id} className="term-row" onClick={() => setDetailId(item.id)}>
                <td className="term-td term-ellipsis" title={termDisplay(item.name)}>{termDisplay(item.name)}</td>
                <td className="term-td term-nowrap">{termDisplay(ASSET_TYPE_LABELS[item.assetType] || item.assetType)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.systemName)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.environment)}</td>
                <td className="term-td term-nowrap">
                  <span className="term-state"><span className="term-dot" style={{ background: meta.color }} />{meta.label}</span>
                </td>
                <td className="term-td term-nowrap">{formatDateTime(item.lastSuccessAt)}</td>
                <td className="term-td term-nowrap">{formatDateTime(item.nextExpectedAt)}</td>
                <td className="term-td term-nowrap">{termDisplay(item.ownerName)}</td>
                <td className="term-td term-nowrap"><span className="term-link">详情</span></td>
              </tr>
            })}
        </tbody>
      </table>
    </Spin>
    <div className="term-foot">状态由最近成功、失败事件与预期周期统一判定；“HTTP 200”或进程存在不单独代表运行正常。</div>
    {detailId !== null && <RuntimeAssetDetail assetId={detailId} onClose={() => setDetailId(null)} />}
  </div>
}
